import { useSyncExternalStore } from 'react';
import { es } from './es';

/**
 * Minimal i18n: English source strings are the keys (gettext style), so untranslated text degrades to
 * readable English instead of raw keys. `{name}` placeholders are interpolated.
 * The store is module-level so plain helpers (formatters, humanize) and components agree on the locale;
 * components subscribe via useI18n() and re-render in place when it changes — no remount, no lost input.
 */
export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', es: 'Español' };

const DICTIONARIES: Record<Locale, Readonly<Record<string, string>>> = { en: {}, es };

let current: Locale = 'en';
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return current;
}

export function setLocale(locale: Locale): void {
  if (locale === current || !LOCALES.includes(locale)) return;
  current = locale;
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  listeners.forEach((l) => l());
}

export function subscribeLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const template = DICTIONARIES[current][key] ?? key;
  return vars ? template.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m)) : template;
}

export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'en';
  return navigator.language.toLowerCase().startsWith('es') ? 'es' : 'en';
}

export function useI18n(): { t: typeof t; locale: Locale; setLocale: typeof setLocale } {
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);
  return { t, locale, setLocale };
}
